import { useEffect, useMemo, useState } from "react"
import type { Meeting, MeetingTopic } from "@/schema"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@ui/shad"
import { badgeVariants } from "@ui/shad/badge"
import { cn } from "@ui/utils"
import { Loader2, X } from "lucide-react"

import { useUpdateMeeting } from "@/hooks/api/meetings"

interface MeetingTopicFormProps {
  meeting: Meeting
  availableTopics: MeetingTopic[]
  autoFocus?: boolean
}

const sortTopics = (topics: MeetingTopic[]) => [...topics].sort((a, b) => a.name.localeCompare(b.name))

export default function MeetingTopicForm({ meeting, availableTopics, autoFocus = true }: MeetingTopicFormProps) {
  const updateMeeting = useUpdateMeeting()
  const selectedTopics = useMemo(() => sortTopics(meeting.meeting_topics ?? []), [meeting.meeting_topics])
  const [optimisticTopics, setOptimisticTopics] = useState(selectedTopics)
  const [searchQuery, setSearchQuery] = useState("")
  const [pendingTopicId, setPendingTopicId] = useState<string | null>(null)

  useEffect(() => setOptimisticTopics(selectedTopics), [selectedTopics])

  const updateTopics = (topics: MeetingTopic[]) => {
    if (updateMeeting.isPending) return

    const previousTopics = optimisticTopics
    setOptimisticTopics(sortTopics(topics))
    updateMeeting.mutate(
      { meetingId: meeting.id, data: { meeting_topic_ids: topics.map((topic) => topic.id) } },
      {
        onError: () => setOptimisticTopics(previousTopics),
        onSettled: () => setPendingTopicId(null),
      }
    )
  }

  const filteredTopics = availableTopics
    .filter((topic) => !topic.archived_at)
    .filter((topic) => !optimisticTopics.some((selected) => selected.id === topic.id))
    .filter((topic) => `${topic.name} ${topic.description}`.toLowerCase().includes(searchQuery.toLowerCase()))

  return (
    <>
      <div className="flex w-[min(30rem,calc(100vw-2rem))] items-center justify-between border-b px-3 py-2">
        <span className="text-sm font-medium">Selected topics</span>
        <span className="text-muted-foreground text-xs tabular-nums">{optimisticTopics.length}</span>
      </div>
      <div className="max-h-64 overflow-y-auto p-3">
        <div className="flex min-h-8 flex-wrap items-center gap-2">
          {optimisticTopics.length === 0 && <span className="text-muted-foreground text-xs">No topics selected</span>}
          {optimisticTopics.map((topic) => (
            <button
              key={topic.id}
              type="button"
              aria-label={`Remove ${topic.name}`}
              disabled={updateMeeting.isPending}
              className={cn(
                badgeVariants({ variant: "secondary" }),
                "hover:bg-tinted cursor-pointer gap-1 disabled:pointer-events-none disabled:opacity-50"
              )}
              onClick={() => {
                setPendingTopicId(topic.id)
                updateTopics(optimisticTopics.filter((selected) => selected.id !== topic.id))
              }}
            >
              {topic.name}
              {pendingTopicId === topic.id ? <Loader2 className="size-3 animate-spin" /> : <X className="size-3" />}
            </button>
          ))}
        </div>
      </div>
      <Command className="rounded-none border-t" shouldFilter={false}>
        <CommandInput
          placeholder="Search topics..."
          value={searchQuery}
          onValueChange={setSearchQuery}
          autoFocus={autoFocus}
          disabled={updateMeeting.isPending}
        />
        <CommandList className="max-h-72">
          <CommandEmpty>{searchQuery ? "No topics found" : "All active topics are selected"}</CommandEmpty>
          <CommandGroup>
            {filteredTopics.map((topic) => (
              <CommandItem
                key={topic.id}
                value={topic.name}
                disabled={updateMeeting.isPending}
                onSelect={() => {
                  setPendingTopicId(topic.id)
                  updateTopics([...optimisticTopics, topic])
                  setSearchQuery("")
                }}
                className="cursor-pointer"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium">{topic.name}</div>
                  <div className="text-muted-foreground truncate text-xs">{topic.description}</div>
                </div>
                {pendingTopicId === topic.id && <Loader2 className="ml-auto size-3 animate-spin" />}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </>
  )
}
